import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  TextField,
} from "@mui/material";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { useCreateProject } from "../../api/use-project";
import { useNavigate } from "react-router-dom";

function NewProject({ open, onClose }) {
  const { t } = useTranslation(["project", "validation", "common"]);
  const navigate = useNavigate();

  const schema = yup.object({
    name: yup
      .string()
      .required(t("requiredValidation", { field: t("projectName") })),
  });

  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm({
    defaultValues: {
      name: "",
    },
    resolver: yupResolver(schema),
  });

  const [{ data, loading, error }, execute] = useCreateProject();

  const onSubmit = async (data) => {
    await execute({ data });
  };

  useEffect(() => {
    if (data) {
      navigate(`/projects/${data.id}`);
    }
  }, [data, navigate]);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("newProject")}</DialogTitle>
      <DialogContent sx={{ overflow: "visible" }}>
        <Box
          component="form"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
          noValidate
          onSubmit={handleSubmit(onSubmit)}
        >
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                fullWidth
                error={!!errors.name}
                helperText={errors.name?.message}
                label={t("projectName")}
                name="name"
                autoFocus
                autoComplete="off"
                {...field}
              />
            )}
          />
          {error && (
            <FormHelperText error>
              {t("genericError", { action: t("createProject") })}
            </FormHelperText>
          )}
        </Box>
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={30} sx={{ mt: 3 }} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("cancel")}</Button>
        <Button onClick={handleSubmit(onSubmit)}>
          {t("create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default NewProject;
